import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";

interface UseDebouncedSearchResult {
  text: string;
  onTextChange: (value: string) => void;
  cancelPending: () => void;
}

export function useDebouncedSearch(
  appliedSearch: string,
  applySearch: (value: string) => void,
  delayMs: number,
): UseDebouncedSearchResult {
  const [text, setText] = useState<string>(appliedSearch);
  const [syncedSearch, setSyncedSearch] = useState<string>(appliedSearch);

  if (appliedSearch !== syncedSearch) {
    setSyncedSearch(appliedSearch);
    setText(appliedSearch);
  }

  const debouncedApply = useMemo(
    () => debounce(applySearch, delayMs),
    [applySearch, delayMs],
  );

  useEffect(() => () => debouncedApply.cancel(), [debouncedApply]);

  const onTextChange = (value: string): void => {
    setText(value);
    debouncedApply(value);
  };

  return {
    text,
    onTextChange,
    cancelPending: () => debouncedApply.cancel(),
  };
}
